import React, { useEffect, useMemo } from "react";

import { Autocomplete, Stack, TextField, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { NumberInput } from "components/forms";
import dayjs from "dayjs";
import { useActionHook } from "hooks";
import { useDateFormatSelect, useDealActions } from "store";
import { IAssetClass, ISubAssetClass } from "store/types";

import { currenciesArray } from "../../../../constants";

interface Props {
  characteristicDataType: "number" | "text" | "percentage" | "currency" | "asset" | "date"; //type of characteristic to define input type
  value: string;
  onChange: (value: string) => void;
  assetId?: number;
  subAssetId?: number | null;
  onChangeAssets?: (value: number) => void;
  isSubAssetClassMode?: boolean; // Flag to indicate we're editing a sub-asset class characteristic
  container?: HTMLElement | null;
}

const Text: React.FC<Props> = ({ value, onChange }) => {
  return <TextField label="Value" onChange={(e) => onChange(e.target.value)} sx={{ width: "50%" }} value={value} />;
};

const Currency: React.FC<Props> = ({ value, onChange, container }) => {
  return (
    <Autocomplete
      options={currenciesArray}
      value={value}
      onChange={(_, option) => onChange(option)}
      disableClearable
      slotProps={{ popper: { container: container ?? undefined } }}
      renderInput={(params) => <TextField {...params} />}
    />
  );
};

const Asset: React.FC<Props> = ({ assetId, subAssetId, onChangeAssets, isSubAssetClassMode, container }) => {
  // In sub-asset class mode, we need the parent assetId to find sub-asset classes
  // In asset class mode, we need assetId for the current value
  const parentAssetId = isSubAssetClassMode ? assetId : undefined;
  const currentValueId = isSubAssetClassMode ? subAssetId : assetId;

  const { getAssetClassesAction } = useDealActions();

  const { data: assetClasses = [], action: getAssetClasses } = useActionHook(getAssetClassesAction);

  useEffect(() => {
    if (!currentValueId && !isSubAssetClassMode) return;
    if (isSubAssetClassMode && !parentAssetId) return;
    getAssetClasses();
  }, []);

  const value: IAssetClass | ISubAssetClass | null = useMemo(() => {
    if (isSubAssetClassMode && parentAssetId && subAssetId) {
      const subAsset =
        assetClasses
          .find((asset: IAssetClass) => asset.id === parentAssetId)
          ?.sub_asset_class.find((subAsset: ISubAssetClass) => subAsset.id === subAssetId) || null;
      return subAsset;
    }
    if (!isSubAssetClassMode && assetId) {
      const asset = assetClasses.find((asset: IAssetClass) => asset.id === assetId) || null;
      return asset;
    }
    return null;
  }, [assetId, subAssetId, isSubAssetClassMode, parentAssetId, assetClasses]);

  const options: IAssetClass[] | ISubAssetClass[] = useMemo(() => {
    if (isSubAssetClassMode && parentAssetId) {
      return assetClasses.find((asset: IAssetClass) => asset.id === parentAssetId)?.sub_asset_class || [];
    }
    return assetClasses;
  }, [assetClasses, isSubAssetClassMode, parentAssetId]);

  if (!currentValueId && !isSubAssetClassMode) return null;
  if (isSubAssetClassMode && !parentAssetId) return null;

  return (
    <Autocomplete
      fullWidth
      options={options as IAssetClass[] | ISubAssetClass[]}
      value={value as IAssetClass | ISubAssetClass}
      renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: IAssetClass | ISubAssetClass) => (
        <li {...props} key={option.id}>
          {option.account_alias || option.presentation_name}
        </li>
      )}
      getOptionLabel={(option) => option.account_alias || option.presentation_name}
      onChange={(_, option) => onChangeAssets && onChangeAssets(option.id)}
      disableClearable
      slotProps={{ popper: { container: container ?? undefined } }}
      renderInput={(params) => <TextField {...params} />}
    />
  );
};

const Percentage: React.FC<Props> = ({ value, onChange }) => {
  return <NumberInput onChange={(e) => onChange(e.target.value)} value={value} numberInputProps={{ suffix: "%" }} />;
};

const Number: React.FC<Props> = ({ value, onChange }) => {
  return <NumberInput onChange={(e) => onChange(e.target.value)} value={value} />;
};

const Date: React.FC<Props> = ({ value, onChange, container }) => {
  const dateFormat = useDateFormatSelect();
  return (
    <DesktopDatePicker
      format={dateFormat}
      views={["year", "month", "day"]}
      slotProps={{
        inputAdornment: { position: "start" },
        popper: {
          container: container ?? undefined,
          sx: {
            minWidth: "335px",
            ".MuiPickersCalendarHeader-labelContainer": {
              fontSize: "0.9rem"
            }
          }
        },
        textField: {
          sx: { padding: 0, svg: { color: "#0a3142" } },
          placeholder: "dd / mm / yyyy",
          fullWidth: true
        }
      }}
      value={value ? dayjs(value) : null}
      label="Date"
      onChange={(value) => {
        onChange(value ? value.toISOString() : "");
      }}
    />
  );
};
const components: Record<Props["characteristicDataType"], React.FC<Props>> = {
  text: Text,
  currency: Currency,
  percentage: Percentage,
  number: Number,
  asset: Asset,
  date: Date
};

export const CharacteristicForm: React.FC<Props> = (props) => {
  const Component = components[props["characteristicDataType"]];

  if (!Component)
    return (
      <Typography sx={{ color: "#0A3142", fontSize: "16px", fontWeight: 400 }}>Invalid characteristic type</Typography>
    );

  return (
    <Stack sx={{ py: "15px" }}>
      <Component {...props} />
    </Stack>
  );
};
